import React from 'react';
import Image from '@plone/volto/components/theme/Image/Image';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { MPFSectionHeading } from 'volto-ministerio-publico-federal/components/UI';

export interface ImageItem {
  '@id'?: string;
  '@type'?: string;
  title?: string;
  description?: string;
  tituloAlternativo?: string | null;
  image_field?: string | null;
  image_scales?: Record<string, Array<Record<string, unknown>>> | null;
  [key: string]: unknown;
}

interface ImagesProps {
  items?: ImageItem[];
  title?: string;
}

/* Mesmas classes do `GaleriaCard` do bloco SubHomeGaleria: moldura no
   formato do design (~8/7), borda sobre o token de outline e zoom sutil no
   hover. O `var(--outline-var)` na mão (e não a utility
   `border-outline-variant`) porque os tokens nunca emitem a var que a
   utility aponta — ver anotação lá. */
const MEDIA_CLASS =
  'relative block aspect-[8/7] overflow-hidden rounded-md border border-[color:var(--outline-var)] bg-surface-bright shadow-center dark:border-night-850 dark:bg-night-700';

const IMAGE_CLASS =
  'absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:transform-none motion-reduce:transition-none';

const CAPTION_CLASS =
  'mt-2 line-clamp-2 text-base font-medium text-on-surface dark:text-night-100';

/** Tem dados de escala suficientes para o `<Image>` do Volto montar um src? */
const hasScaleData = (item: ImageItem): boolean => {
  const field = item.image_field;
  if (!field) return false;
  const scales = item.image_scales?.[field];
  return Array.isArray(scales) && scales.length > 0;
};

/**
 * Grade de miniaturas para itens `Image` de uma listagem (pastas,
 * coleções). Cada card linka para a página da imagem; sem dados de escala
 * na API, o card degrada para legenda com link — nunca um quadro vazio.
 *
 * `not-prose` desliga a tipografia herdada de `[class*='__editor-content']`
 * quando renderizado dentro do conteúdo interno (mesmo recurso do bloco
 * SubHomeGaleria).
 */
export const Images: React.FC<ImagesProps> = ({ items = [], title }) => {
  const images = items.filter(
    (item) => item['@type'] === 'Image' && !!item['@id'],
  );

  if (images.length === 0) {
    return null;
  }

  return (
    <div className="mpf__images not-prose">
      {title && <MPFSectionHeading title={title} />}

      <ul className="mpf__images-grid grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-4">
        {images.map((item) => {
          const caption = item.description || item.title;
          const alt =
            item.tituloAlternativo || item.description || item.title || '';

          return (
            <li key={item['@id']} className="mpf__images-item min-w-0">
              <figure className="group relative m-0">
                <UniversalLink
                  item={{ '@id': item['@id'], '@type': item['@type'] }}
                  className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary after:absolute after:inset-0"
                  title={item.title}
                >
                  {hasScaleData(item) && (
                    <span className={MEDIA_CLASS}>
                      <Image
                        item={item as never}
                        imageField={item.image_field as string}
                        alt={alt}
                        loading="lazy"
                        className={IMAGE_CLASS}
                      />
                    </span>
                  )}

                  {caption && (
                    <figcaption className={CAPTION_CLASS}>{caption}</figcaption>
                  )}
                </UniversalLink>
              </figure>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Images;
